import { LocalizedCurrencyValue, LocalizedText, useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack, Body, Divider } from '@optimus-web/core';
import { SimpleAmount } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { HighlightScore, ProgressChart } from '@mews/b2b-ui';
import { TextMessagingLabel } from '../../Typography';
import { SmsGuideLink } from '../../SmsGuideLink';
import { isOverBudget } from './isOverBudget';

interface AvailableBudgetProps {
    remainingBudget: SimpleAmount;
    totalBudget: SimpleAmount;
}

export const AvailableBudget = ({ remainingBudget, totalBudget }: AvailableBudgetProps) => {
    const localizeText = useLocalizeText();

    const overBudget = isOverBudget(remainingBudget);

    const scoreSuffix = (
        <>
            {UnicodeEntity.ForwardSlash}
            {UnicodeEntity.Space}
            <LocalizedCurrencyValue
                value={{
                    value: totalBudget.value,
                    currency: totalBudget.currencyCode,
                }}
            />
        </>
    );

    const remainingValue = (
        <LocalizedCurrencyValue
            value={{
                value: remainingBudget.value,
                currency: remainingBudget.currencyCode,
            }}
        />
    );

    return (
        <Stack vertical spacing="200" data-test-id="available-budget-score-container">
            <Stack vertical spacing="300">
                <Stack vertical>
                    <Stack vertical spacing="150" distribution="stretch" fill>
                        <TextMessagingLabel as="p">
                            <LocalizedText textKey={TextKey.TextMessagingConfigurationLabelAvailableBudget} />
                        </TextMessagingLabel>
                        <HighlightScore suffix={scoreSuffix} value={remainingValue} />
                        <ProgressChart
                            animated
                            data={[
                                {
                                    id: 'availableBudget',
                                    value: overBudget ? totalBudget.value : remainingBudget.value,
                                    name: localizeText({
                                        textKey: TextKey.TextMessagingConfigurationLabelAvailableBudget,
                                    }),
                                    color: overBudget ? 'red' : 'green',
                                },
                            ]}
                            title={localizeText({ textKey: TextKey.TextMessagingConfigurationLabelAvailableBudget })}
                            total={totalBudget.value}
                        />
                    </Stack>
                </Stack>
                <Body size="small" variant="secondary">
                    <LocalizedText
                        textKey={TextKey.TextMessagingConfigurationUnusedBalanceDescriptionAvailableBudget}
                        substitutes={{ helpArticle: (text) => <SmsGuideLink text={text} /> }}
                    />
                </Body>
            </Stack>
            <Divider />
        </Stack>
    );
};
